/**
 * Oracle Resolver — Engine Extras helpers.
 *
 * Pure source-text utilities + static catalogs consumed by the dialect-native
 * Oracle resolver to surface:
 *   - `seq.NEXTVAL` / `seq.CURRVAL` after a sequence dot,
 *   - hint names inside Oracle hint comments (`slash-star-plus ... star-slash`),
 *   - Oracle optimizer hint catalog.
 *
 * No Monaco / electron / React imports — safe for tests + renderer.
 */

import type { OffsetRange } from '@/core/engines';

/** Oracle pseudo-method names accessible via `seq.<here>`. */
export const ORACLE_SEQUENCE_METHODS: readonly string[] = ['NEXTVAL', 'CURRVAL'];

export type OracleHintName = {
  readonly name: string;
  readonly detail: string;
  /**
   * True when the hint accepts one or more table aliases inside parens
   * (e.g. `INDEX(t idx)` or `USE_HASH(t1 t2)`). Drives whether the snippet
   * inserts a parenthesized argument placeholder.
   */
  readonly takesAliasArg: boolean;
};

/**
 * Curated catalog of common Oracle optimizer hints. Each entry surfaces as a
 * tier-6 snippet candidate inside an Oracle hint comment block. The list is
 * intentionally short — the goal is to cover the optimizer-tuning hints
 * users reach for most often, not to mirror every hint Oracle's parser
 * accepts. Easy to extend without touching the scanner.
 */
export const ORACLE_HINT_NAMES: readonly OracleHintName[] = [
  // Optimizer mode
  { name: 'ALL_ROWS', detail: 'Optimize for total throughput', takesAliasArg: false },
  { name: 'FIRST_ROWS', detail: 'Optimize for first-N rows', takesAliasArg: false },
  // Single-table access path
  { name: 'INDEX', detail: 'Use index access', takesAliasArg: true },
  { name: 'NO_INDEX', detail: 'Disable index access', takesAliasArg: true },
  { name: 'INDEX_FFS', detail: 'Index fast full scan', takesAliasArg: true },
  { name: 'INDEX_SS', detail: 'Index skip scan', takesAliasArg: true },
  { name: 'FULL', detail: 'Full table scan', takesAliasArg: true },
  // Join methods
  { name: 'USE_HASH', detail: 'Hash join', takesAliasArg: true },
  { name: 'USE_NL', detail: 'Nested-loops join', takesAliasArg: true },
  { name: 'USE_MERGE', detail: 'Sort-merge join', takesAliasArg: true },
  { name: 'LEADING', detail: 'Join leading table', takesAliasArg: true },
  { name: 'ORDERED', detail: 'Join in FROM order', takesAliasArg: false },
  // Parallel + DML
  { name: 'PARALLEL', detail: 'Parallel execution', takesAliasArg: true },
  { name: 'NO_PARALLEL', detail: 'Disable parallel', takesAliasArg: true },
  { name: 'APPEND', detail: 'Direct-path INSERT', takesAliasArg: false },
  { name: 'NOAPPEND', detail: 'Conventional INSERT', takesAliasArg: false },
  // CTE materialization
  { name: 'MATERIALIZE', detail: 'Materialize CTE', takesAliasArg: false },
  { name: 'INLINE', detail: 'Inline CTE', takesAliasArg: false },
  // Misc
  { name: 'GATHER_PLAN_STATISTICS', detail: 'Capture row-source stats', takesAliasArg: false },
  { name: 'RESULT_CACHE', detail: 'Cache result in shared pool', takesAliasArg: false },
  { name: 'DRIVING_SITE', detail: 'Choose distributed driving site', takesAliasArg: true },
  { name: 'CACHE', detail: 'Buffer-cache MRU', takesAliasArg: true },
  { name: 'NOCACHE', detail: 'Buffer-cache LRU', takesAliasArg: true },
];

export type EnclosingHint = {
  /** Half-open offsets of the surrounding hint comment. */
  readonly range: OffsetRange;
  /** In-progress identifier-shaped prefix at the cursor (may be empty). */
  readonly typedPrefix: string;
};

/**
 * Returns the enclosing Oracle hint comment when the cursor sits strictly
 * inside its body. Returns null when the cursor is outside any comment, or
 * inside a plain block comment that lacks the `+` modifier.
 *
 * Tree-sitter parks Oracle hint comments under `marginalia` so the resolver's
 * normal `insideStringOrComment` check returns true. The resolver consults
 * this scanner BEFORE that early-bail so it can emit hint candidates instead.
 *
 * The scan walks back from `offset` until either:
 *   1. we hit a hint open — return the bounded range, OR
 *   2. we hit a plain comment open — return null, OR
 *   3. we hit a comment close — we're past the previous comment's close,
 *      return null.
 *
 * The forward end-walk finds the comment close or EOF (an unterminated hint
 * running to EOF is treated as still inside the hint).
 */
export function findEnclosingOracleHint(source: string, offset: number): EnclosingHint | null {
  if (offset < 0 || offset > source.length) return null;

  let i = offset - 1;
  let openIdx = -1;
  while (i >= 1) {
    const c = source[i]!;
    const prev = source[i - 1]!;
    if (prev === '*' && c === '/') {
      // Comment close before us — not inside any block comment.
      return null;
    }
    if (prev === '/' && c === '*') {
      openIdx = i - 1;
      break;
    }
    i--;
  }
  if (openIdx === -1) return null;

  // Inspect the char after the `*` to see if this is a hint comment.
  const afterStar = source[openIdx + 2];
  if (afterStar !== '+') return null;

  // Walk forward to find the comment close (or EOF for unterminated hint).
  let endIdx = source.length;
  for (let j = offset; j < source.length - 1; j++) {
    if (source[j] === '*' && source[j + 1] === '/') {
      endIdx = j + 2;
      // Cursor strictly outside body when at or past the closing `*`.
      if (offset >= j) return null;
      break;
    }
  }

  // Compute typed prefix — walk back through bare-ident chars from cursor.
  let prefixStart = offset;
  while (prefixStart > openIdx + 3 && isHintIdentChar(source[prefixStart - 1]!)) {
    prefixStart--;
  }
  const typedPrefix = source.slice(prefixStart, offset);

  return {
    range: { start: openIdx, end: endIdx },
    typedPrefix,
  };
}

function isHintIdentChar(ch: string): boolean {
  return (
    (ch >= 'A' && ch <= 'Z') ||
    (ch >= 'a' && ch <= 'z') ||
    (ch >= '0' && ch <= '9') ||
    ch === '_'
  );
}
